import { useCallback, useEffect, useRef, useState } from 'react';
import { Employee } from '../../../types';
import { deleteEmployeeApi, fetchEmployeesApi } from './api';

/**
 * §ADM-3. Список співробітників компанії: підвантаження сторінками без втрат.
 *
 * ЩО БУЛО ЗЛАМАНО. У компанії з 91 співробітником панель показувала 51, у компанії
 * з 40 — рівно 30. Обидві недостачі — точні кратні розміру сторінки (10), бо
 * ЦІЛІ сторінки мовчки губились. Механізм складався з трьох частин:
 *   1) прапорець «йде завантаження» передавався у список як `ref`
 *      (`isFetching={isFetchingRef.current}`). React не перемальовує компонент,
 *      коли змінюється ref, тож охорона в списку бачила ЗАСТАРІЛЕ значення;
 *   2) сторінку просили два незалежні джерела — обробник скролу і ефект
 *      автодоливання (§FB39c), тож `pageIndex` міг перестрибнути вперед, поки
 *      попередній запит ще був у дорозі;
 *   3) завантажувач починався з `if (isFetchingRef.current) return;` — МОВЧКИ
 *      виходив. А `pageIndex` уже збільшено, тож `useEffect([pageIndex])` для
 *      цього значення більше ніколи не спрацьовував: сторінка втрачена назавжди.
 * Люди зникали З СЕРЕДИНИ списку (кінець досягався), тому на око пропажу не
 * видно — і потрібну людину неможливо було знайти, щоб призначити їй роль.
 * Бекенд і БД при цьому чисті: перевірено на всіх 481 компаніях, розбіжностей 0.
 *
 * ЯК ЗРОБЛЕНО ЗАРАЗ.
 *   • Сторінки складаємо в МАПУ `page -> items`, а список збираємо з неї в
 *     порядку ключів. Порядок правильний і дублів немає незалежно від того, в
 *     якій послідовності повернулись відповіді.
 *   • Замість «зайнято — вихід» працює ДОГОНЯЮЧИЙ ЦИКЛ: один активний
 *     завантажувач крутиться, доки не добере всі сторінки до `targetPageRef`.
 *     Якщо `pageIndex` стрибнув уперед під час запиту, цикл це побачить на
 *     наступній ітерації й добере пропущене. Втратити сторінку неможливо.
 *   • «Йде завантаження» — справжній стан (`useState`), тож список бачить живе
 *     значення. Ref лишається тільки мютексом на час роботи циклу.
 *   • Покоління (`genRef`) знецінює відповіді попереднього набору: зміна
 *     компанії, пошуку чи сортування більше не може «долити» чужі рядки.
 */
export const useEmployeeData = (
  selectedClientId: number | null,
  orderDirection: 'ASC' | 'DESC',
  searchText: string,
) => {
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [pageIndex, setPageIndex] = useState<number>(0);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [isFetching, setIsFetching] = useState<boolean>(false);

  // Завантажені сторінки: ключ — номер сторінки (0-based), значення — її рядки.
  const pagesRef = useRef<Map<number, Employee[]>>(new Map());
  // До якої сторінки включно треба добрати. Цикл перечитує це ЩОРАЗУ.
  const targetPageRef = useRef<number>(0);
  // Мютекс: рівно один активний завантажувач.
  const runningRef = useRef<boolean>(false);
  // Покоління набору даних. Інкремент = усі відповіді в дорозі стають нецікаві.
  const genRef = useRef<number>(0);
  // Чинний рядок пошуку. §ADM-3: раніше сторінки при прокрутці довантажувались
  // із порожнім рядком (хук отримує searchText='' — пошук живе в окремому хуці),
  // тож до відфільтрованого списку доливались НЕФІЛЬТРОВАНІ рядки. Тепер фільтр
  // зберігається тут і застосовується до кожної наступної сторінки.
  const searchRef = useRef<string>(searchText);

  const rebuild = useCallback(() => {
    const ordered = Array.from(pagesRef.current.keys()).sort((a, b) => a - b);
    setEmployees(ordered.flatMap((p) => pagesRef.current.get(p) ?? []));
  }, []);

  /** Добирає всі ще не завантажені сторінки до targetPageRef включно. */
  const runLoader = useCallback(async () => {
    if (selectedClientId === null || runningRef.current) return;
    runningRef.current = true;
    setIsFetching(true);
    const gen = genRef.current;
    try {
      for (let page = 0; page <= targetPageRef.current; page++) {
        if (gen !== genRef.current) return; // набір скинули — виходимо тихо
        if (pagesRef.current.has(page)) continue;

        const data = await fetchEmployeesApi(
          selectedClientId,
          page,
          orderDirection,
          searchRef.current,
        );
        if (gen !== genRef.current) return;

        pagesRef.current.set(page, data.items ?? []);
        rebuild();
        setHasMore(data.hasNextPage);
        // Дійшли до кінця — далі добирати нічого, навіть якщо скрол устиг
        // попросити більше.
        if (!data.hasNextPage) break;
      }
    } catch (error) {
      console.error('Помилка при завантаженні даних:', error);
    } finally {
      // Прапорці чіпає лише «своє» покоління: інакше завершення старого
      // запиту зняло б ознаку зайнятості з уже нового набору.
      if (gen === genRef.current) {
        runningRef.current = false;
        setIsFetching(false);
      }
    }
  }, [selectedClientId, orderDirection, rebuild]);

  /** Повне перечитування з нуля (зміна компанії, пошуку, сортування, видалення). */
  const resetAndLoad = useCallback(
    (search: string) => {
      genRef.current += 1; // усе, що в дорозі, знецінено
      pagesRef.current.clear();
      targetPageRef.current = 0;
      runningRef.current = false; // новий набір стартує одразу
      searchRef.current = search;
      setEmployees([]);
      setHasMore(true);
      setPageIndex(0);
      void runLoader();
    },
    [runLoader],
  );

  /**
   * Зовнішній інтерфейс лишився тим самим, щоб не чіпати пошук і видалення:
   * `reset = true` — перечитати з нуля, інакше — добрати до вказаної сторінки.
   */
  const fetchEmployees = useCallback(
    (page: number, reset = false, searchString = '') => {
      if (selectedClientId === null) return;
      if (reset) {
        resetAndLoad(searchString);
        return;
      }
      if (page > targetPageRef.current) targetPageRef.current = page;
      void runLoader();
    },
    [selectedClientId, resetAndLoad, runLoader],
  );

  // Зміна компанії або сортування — повне перечитування. Чинний фільтр пошуку
  // ЗБЕРІГАЄМО (searchRef), інакше сортування мовчки скидало б пошук: у хук
  // приходить порожній рядок, бо пошук живе в окремому хуці.
  useEffect(() => {
    if (selectedClientId !== null) {
      resetAndLoad(searchRef.current);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClientId, orderDirection]);

  // Скрол попросив наступну сторінку. Ціль піднімаємо ЗАВЖДИ — навіть якщо
  // завантажувач зараз зайнятий: він добере її в тому ж циклі.
  useEffect(() => {
    if (selectedClientId === null || pageIndex === 0) return;
    if (pageIndex > targetPageRef.current) targetPageRef.current = pageIndex;
    void runLoader();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageIndex]);

  return {
    employees,
    hasMore,
    pageIndex,
    setPageIndex,
    fetchEmployees,
    setHasMore,
    isFetching,
  };
};

export const useDeleteEmployee = (
  fetchEmployees: (page: number, reset: boolean, searchString: string) => void,
  selectedClientId: number | null,
  searchText: string,
) => {
  const deleteEmployee = async (workerId: string) => {
    try {
      const status = await deleteEmployeeApi(workerId, selectedClientId);
      if (status === 200) {
        // §ADM-3: раніше це перечитування могло мовчки не відбутись, якщо в цей
        // момент тривало підвантаження — видалений співробітник лишався в списку.
        fetchEmployees(0, true, searchText);
      }
    } catch (error) {
      console.error('Помилка при видаленні працівника:', error);
    }
  };

  return deleteEmployee;
};

export const useSearch = (
  fetchEmployees: (page: number, reset: boolean, searchString: string) => void,
) => {
  const [searchText, setSearchText] = useState<string>('');
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchText(value);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (value.length === 0 || value.length >= 2) {
      searchTimeoutRef.current = setTimeout(() => {
        // §ADM-3: так само — пошук більше не «з'їдається» під час підвантаження.
        fetchEmployees(0, true, value);
      }, 1000);
    }
  };

  const handleClearSearch = () => {
    setSearchText('');
    fetchEmployees(0, true, '');
  };

  return {
    searchText,
    handleSearchChange,
    handleClearSearch,
  };
};
